'use client';

import { getFirebaseConfig } from '@/utils/firebase.service';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface StoreContextType {
  storeUrl: string;
  subOrgId: string;
  refetch: () => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const useStoreContext = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStoreContext must be used within StoreProvider');
  }
  return context;
};

interface StoreProviderProps {
  children: ReactNode;
}

const getStoreAndActivityKitsUrlFromFirebase = async (key: string) => {
  return await getFirebaseConfig(key);
};

type StoreConfigItem = {
  env: string;
  url: string;
  sub_org_id?: string;
};

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const [storeUrl, setStoreUrl] = useState<string>('');
  const [subOrgId, setSubOrgId] = useState<string>('');

  const fetchPwStoreUrl = async () => {
    try {
      const envKey = process.env.NEXT_PUBLIC_PW_STORE_KEY as string;

      const urlData = await getStoreAndActivityKitsUrlFromFirebase(envKey);

      const parsedData = typeof urlData === 'string' ? JSON.parse(urlData) : urlData;

      const appEnv = process.env.NEXT_PUBLIC_STORE_ENVIRONMENT?.toLowerCase();

      const matchedEnvConfig = Array.isArray(parsedData)
        ? (parsedData as StoreConfigItem[]).find(item => item?.env?.toLowerCase() === appEnv)
        : undefined;

      const storeUrl = matchedEnvConfig?.url || '';
      const subOrgId = matchedEnvConfig?.sub_org_id || '';

      setStoreUrl(storeUrl);
      setSubOrgId(subOrgId);
    } catch (err) {
      console.error('Error fetching PW Store URL:', err);
    }
  };

  useEffect(() => {
    fetchPwStoreUrl();
  }, []);

  const value = {
    storeUrl,
    subOrgId,
    refetch: fetchPwStoreUrl,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
